// 2026-05-26 - FormuLearning - Step 1 특허 정보 섹션
// 2026-09-09 수정 - 이슈 #72(M901-09): **분류 실패를 화면이 말한다.** 종전에는 LLM 응답이
//   잘려 전량 미분류가 돼도 유형 칸만 비어 사용자가 «분류가 원래 없나» 로 읽었다
//   (valsartan 50건이 6개 프로젝트에서 그렇게 남았다). 분류·미분류·실패 건수를 세어
//   집계 문구에 덧붙인다.
// 2026-09-23 수정 - 이슈 #87: KIPRIS 조회 실패(`meta.fetch_failed`)를 «결과 없음»과 구분해 알린다.
// 파일 위치: frontend/src/features/step1/PatentSection.tsx

import { Fragment, useEffect, useState } from 'react'
import type { PatentMeta } from '../../api/step1'
import Collecting from './Collecting'

interface Props {
  patents: Record<string, string | null>[]
  meta?: PatentMeta
  loading?: boolean
}

function fmtDate(v: string | null | undefined): string | null {
  if (!v) return null
  const s = String(v)
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s
}

function fmt(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  return String(v)
}

// 2026-06-27 - Google Patents 링크 생성 (#24-8)
// KIPRIS 등록번호(13자리 packed = 9자리 권리번호 + 4자리 부기번호 0000)를
// Google Patents 공개번호로 변환해 해당 특허로 바로 가는 딥링크를 만든다.
// 예: 1014408080000 → KR101440808 → patents.google.com/patent/KR101440808 (정확 매칭).
// 등록번호가 아니면(출원번호 폴백·외국번호 등) 제목 필드(TI=) 검색으로 폴백한다.
function googlePatentUrl(patentNo: unknown, title: string | null): string | null {
  let core = String(patentNo ?? '').replace(/\D/g, '')
  if (core.length > 9 && core.endsWith('0000')) core = core.slice(0, -4)  // 부기번호 제거
  if ((core.length === 8 || core.length === 9) && (core.startsWith('10') || core.startsWith('20'))) {
    return `https://patents.google.com/patent/KR${core}`
  }
  return title ? `https://patents.google.com/?q=${encodeURIComponent(`(TI="${title}")`)}` : null
}

// 분류 카테고리 표시·정렬 순서(컴마결합 다중태깅의 개별 태그 단위).
const TYPE_ORDER = ['물질', '제형/조성물', '제법', '용도/용법', '관련없음']

// 컴마결합 type 문자열을 개별 태그 배열로 분해. 예: "제형/조성물, 용도/용법" → ['제형/조성물','용도/용법'].
function typeTags(v: unknown): string[] {
  const s = fmt(v)
  return s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []
}

// 정렬 비교 키. type은 카테고리 순서(첫 태그 인덱스)로, 나머지는 문자열로.
function sortVal(p: Record<string, string | null>, key: SortKey): string | number {
  if (key === 'type') {
    const first = typeTags(p.type)[0]
    const idx = first ? TYPE_ORDER.indexOf(first) : -1
    return idx < 0 ? Number.MAX_SAFE_INTEGER : idx
  }
  if (key === 'application_date') return String(p.application_date ?? '').replace(/\D/g, '')
  return (fmt(p[key]) ?? '').toLowerCase()
}

type SortKey = 'type' | 'application_date' | 'holder' | 'title'

export default function PatentSection({ patents, meta, loading }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null)
  const [zoomImg, setZoomImg] = useState<string | null>(null)  // 대표도면 확대 팝업(분자구조와 동일 패턴)
  const [filterTypes, setFilterTypes] = useState<string[]>([])  // 선택된 type 태그(OR 필터, 비면 전체)
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)

  useEffect(() => {
    if (!zoomImg) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomImg(null) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [zoomImg])

  const total = meta?.total_search_count
  const registered = meta?.registered_found

  // 특허에 등장하는 개별 type 태그 모음(카테고리 순서대로). 필터 버튼 재료.
  const availableTypes = TYPE_ORDER.filter((t) => patents.some((p) => typeTags(p.type).includes(t)))

  const toggleFilter = (t: string) =>
    setFilterTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]))

  // 헤더 클릭: 무순 → 오름 → 내림 → 무순 토글(한 번에 한 컬럼만).
  const toggleSort = (key: SortKey) =>
    setSort((cur) =>
      cur?.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null,
    )

  // 원본 인덱스를 보존(펼침 상태 안정화) + 필터 + 정렬 적용한 표시용 뷰.
  let view = patents.map((p, i) => ({ p, i }))
  if (filterTypes.length > 0) {
    view = view.filter(({ p }) => typeTags(p.type).some((t) => filterTypes.includes(t)))
  }
  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1
    view = [...view].sort((a, b) => {
      const va = sortVal(a.p, sort.key)
      const vb = sortVal(b.p, sort.key)
      if (va === vb) return 0
      if (va === '') return 1  // 빈 값은 방향과 무관하게 뒤로
      if (vb === '') return -1
      return va < vb ? -dir : dir
    })
  }

  const countLabel =
    total != null
      ? `KIPRIS 전체 ${total.toLocaleString()}건 중 등록 ${registered ?? 0}건 분석 · 관련 ${patents.length}건` +
        (filterTypes.length > 0 ? ` · 필터 ${view.length}건 표시` : ' 표시')
      : null

  // 2026-09-09 - 이슈 #72(M901-09): 유형 분류 성패. `meta.classified` 는 이번 검색에서
  //   분류된 건수이고, 표에 남은 것 중 유형이 빈 건이 미분류다. 옛 스냅샷은 meta가 없어
  //   `classified` 가 undefined 인데, 그때는 세지 않는다(없는 사실을 지어내지 않는다).
  const unclassified = patents.filter((p) => !p.type).length
  const classifyFailed = meta?.classify_failed ?? 0
  const classifyLabel =
    meta?.classified == null && unclassified === 0
      ? null
      : classifyFailed > 0
        ? `유형 분류 실패 ${classifyFailed}건 — 재검색하면 다시 시도합니다`
        : unclassified > 0
          ? `유형 미분류 ${unclassified}건`
          : null

  // 2026-09-23 - 이슈 #87: KIPRIS 조회 실패 검색어. 비어 있으면 «결과 없음», 있으면 «조회 실패»로 알린다.
  const fetchFailed = meta?.fetch_failed ?? []

  const sortMark = (key: SortKey) => (sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  return (
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Patent Information</h4>
        {countLabel && <span className="text-[10px] text-text-muted">{countLabel}</span>}
        {classifyLabel && (
          <span
            className="rounded px-1.5 py-0.5 text-[10px]"
            style={{
              color: classifyFailed > 0 ? 'var(--color-warn)' : 'var(--color-text-muted)',
              background: classifyFailed > 0
                ? 'color-mix(in srgb, var(--color-warn) 14%, transparent)'
                : 'var(--color-bg-panel)',
            }}
          >
            {classifyFailed > 0 ? '⚠ ' : ''}{classifyLabel}
          </span>
        )}
      </div>
      {availableTypes.length > 0 && (
        // type 태그 필터 — 클릭 토글, 선택분이 있으면 해당 태그를 포함한 특허만 표시(OR)
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {availableTypes.map((t) => {
            const on = filterTypes.includes(t)
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleFilter(t)}
                className="rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors"
                style={{
                  borderColor: on ? 'var(--color-accent-purple)' : 'var(--border-subtle)',
                  background: on ? 'var(--bg-chip-purple)' : 'transparent',
                  color: on ? 'var(--color-accent-purple)' : 'var(--text-muted)',
                }}
              >
                {t}
              </button>
            )
          })}
          {filterTypes.length > 0 && (
            <button
              type="button"
              onClick={() => setFilterTypes([])}
              className="px-1.5 text-[10px] underline"
              style={{ color: 'var(--text-muted)' }}
            >
              초기화
            </button>
          )}
        </div>
      )}
      {!loading && fetchFailed.length > 0 && (
        <p className="mb-2 text-[11px]" style={{ color: 'var(--color-warn)' }}>
          ⚠ {patents.length === 0 ? '특허 조회 실패' : '일부 검색어 특허 조회 실패'} — KIPRIS 가 응답하지 않았습니다
          ({fetchFailed.join(', ')}). 재검색하면 다시 조회합니다.
        </p>
      )}
      {patents.length === 0 ? (
        loading ? <Collecting /> : fetchFailed.length > 0 ? null : <p className="text-[11px] text-text-muted">표시할 특허 정보가 없습니다.</p>
      ) : (
        <div className="panel-in overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th onClick={() => toggleSort('type')} className="cursor-pointer select-none whitespace-nowrap px-3 py-1.5 text-left font-medium text-text-secondary hover:text-text-body">Type{sortMark('type')}</th>
                <th onClick={() => toggleSort('application_date')} className="cursor-pointer select-none whitespace-nowrap px-3 py-1.5 text-center font-medium text-text-secondary hover:text-text-body">Application Date{sortMark('application_date')}</th>
                <th onClick={() => toggleSort('holder')} className="cursor-pointer select-none px-3 py-1.5 text-left font-medium text-text-secondary hover:text-text-body">Holder{sortMark('holder')}</th>
                <th onClick={() => toggleSort('title')} className="cursor-pointer select-none px-3 py-1.5 text-left font-medium text-text-secondary hover:text-text-body">Title{sortMark('title')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-text-secondary"></th>
              </tr>
            </thead>
            <tbody>
              {view.map(({ p, i }) => {
                const open = expanded === i
                const details: [string, string | null][] = [
                  ['Patent No.', fmt(p.patent_no)],
                  ['Holder', fmt(p.holder)],
                  ['Application No.', fmt(p.application_no)],
                  ['Registration Date', fmtDate(p.register_date)],
                  ['Publication Date', fmtDate(p.public_date)],
                  ['IPC Classification', fmt(p.ipc)],
                ]
                // 2026-06-26 - KIPRIS 상세 링크 제거 → 구글 검색 링크로 대체 (#24-8)
                // 2026-06-27 - 등록번호가 있으면 Google Patents 특정 특허 딥링크(KR<번호>),
                // 없으면 제목 필드(TI=) 검색. Patents 검색이 들쭉날쭉해 일반 Google 검색도 병기.
                const title = fmt(p.title)
                const googlePatentsLink = googlePatentUrl(p.patent_no, title)
                const googleSearchLink = title ? `https://www.google.com/search?q=${encodeURIComponent(title)}` : null
                const hasDetail = fmt(p.summary) || details.some(([, v]) => v) || fmt(p.thumbnail) || googlePatentsLink
                return (
                  <Fragment key={i}>
                    <tr
                      onClick={() => setExpanded(open ? null : i)}
                      className="cursor-pointer border-t"
                      style={{ borderColor: 'var(--border-subtle)', backgroundColor: open ? 'var(--bg-row-active)' : 'transparent' }}
                    >
                      <td className="px-3 py-1.5">
                        <span className="inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>
                          {fmt(p.type)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-center text-text-body">{fmtDate(p.application_date)}</td>
                      <td className="max-w-[145px] truncate px-3 py-1.5 text-text-muted">{fmt(p.holder)}</td>
                      <td className="px-3 py-1.5 text-text-muted">{fmt(p.title)}</td>
                      <td className="px-3 py-1.5 text-right text-[9px] text-text-secondary">{hasDetail ? (open ? '▲' : '▼') : ''}</td>
                    </tr>
                    {open && hasDetail && (
                      <tr style={{ backgroundColor: 'var(--bg-row-active)' }}>
                        <td colSpan={5} className="px-3 py-2">
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                                {details.map(([label, v]) =>
                                  v ? (
                                    <div key={label} className="contents">
                                      <dt className="whitespace-nowrap font-medium text-text-secondary">{label}</dt>
                                      <dd className="text-text-body">{v}</dd>
                                    </div>
                                  ) : null,
                                )}
                              </dl>
                              {fmt(p.summary) && (
                                <div className="mt-2">
                                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Abstract</p>
                                  <p className="mt-0.5 leading-relaxed text-text-body">{fmt(p.summary)}</p>
                                </div>
                              )}
                              {(googlePatentsLink || googleSearchLink) && (
                                <div className="mt-2 flex flex-wrap items-center gap-3">
                                  {googlePatentsLink && (
                                    <a
                                      href={googlePatentsLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-block text-[10px] font-medium underline"
                                      style={{ color: 'var(--color-accent-purple)' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Google Patents ↗
                                    </a>
                                  )}
                                  {googleSearchLink && (
                                    <a
                                      href={googleSearchLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-block text-[10px] font-medium underline"
                                      style={{ color: 'var(--color-accent-purple)' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Google 검색 ↗
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                            {fmt(p.thumbnail) && (
                              <img
                                src={fmt(p.thumbnail) as string}
                                alt="대표도면"
                                className="h-28 w-28 shrink-0 cursor-zoom-in rounded border object-contain transition-shadow hover:shadow-md"
                                style={{ borderColor: 'var(--border-soft)', background: '#fff' }}
                                title="클릭하면 원본 크게 보기"
                                onClick={(e) => { e.stopPropagation(); setZoomImg((fmt(p.drawing) || fmt(p.thumbnail)) as string) }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {patents.length > 0 && (
        // 2026-06-26 - 'Vertex AI' 문구 제거 + 강조 완화(경고 박스→잔잔한 muted 한 줄) (#24-10)
        <p className="mt-2 px-1 text-[10px] leading-relaxed text-text-muted">
          분류 결과는 참고용이며, 실제 특허 침해 여부는 전문가 확인이 필요합니다.
        </p>
      )}
      {/* 2026-06-28 - 대표도면 확대 팝업(분자구조 이미지와 동일 패턴, 약 3배 336px) */}
      {zoomImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setZoomImg(null)}
        >
          <div
            className="relative rounded-lg p-4"
            style={{ background: '#fff', boxShadow: 'var(--elev-2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomImg(null)}
              className="absolute -right-3 -top-3 flex h-7 w-7 items-center justify-center rounded-full bg-bg-card text-text-secondary hover:text-text-body"
              style={{ boxShadow: 'var(--elev-2)' }}
              aria-label="닫기"
            >✕</button>
            <img
              src={zoomImg}
              alt="대표도면"
              className="cursor-zoom-out rounded object-contain"
              style={{ maxWidth: 500, maxHeight: 500, background: '#fff' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
